import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Download, ChartBar } from "@phosphor-icons/react";

const COLORS = ["#D4AF37", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899"];

export default function Reports() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/reports/summary", { params: { period } }).then(r => setData(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, [period]);

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ["Metric", "Value"],
      ["Period", data.period],
      ["Total Leads", data.total_leads],
      ["Closed", data.closed],
      ["Lost", data.lost],
      ["Conversion %", data.conversion],
      [],
      ["By Source", "Count"],
      ...Object.entries(data.by_source),
      [],
      ["By Project", "Count"],
      ...Object.entries(data.by_project),
      [],
      ["By Executive", "Count"],
      ...Object.entries(data.by_executive).map(([k, v]) => [userMap[k] || k, v]),
    ];
    const csv = rows.map(r => r.map(v => `"${v ?? ""}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `nawab-report-${period}.csv`; a.click();
  };

  if (!data) return <div className="text-slate-400">Loading…</div>;

  const sourceData = Object.entries(data.by_source).map(([name, value]) => ({ name, value }));
  const projectData = Object.entries(data.by_project).map(([name, value]) => ({ name, value }));
  const execData = Object.entries(data.by_executive).map(([k, v]) => ({ name: userMap[k] || "Unassigned", value: v }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Analytics</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">Reports</h1>
        </div>
        <div className="flex gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} data-testid="report-period"
            className="bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-4 py-2 text-sm text-white">
            <option value="daily">Today</option>
            <option value="weekly">Last 7 days</option>
            <option value="monthly">Last 30 days</option>
          </select>
          <button onClick={exportCSV} data-testid="export-csv-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">
            <Download weight="bold" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[["Total Leads", data.total_leads], ["Closed", data.closed], ["Lost", data.lost], ["Conversion", `${data.conversion}%`]].map(([l, v]) => (
          <div key={l} className="card-luxury p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
            <div className="font-serif text-3xl text-white mt-2">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxury p-6">
          <div className="flex items-center gap-2 mb-4"><ChartBar className="text-[#D4AF37]" weight="duotone" /><h3 className="font-serif text-lg text-white">By Source</h3></div>
          <div className="h-64">
            <ResponsiveContainer><BarChart data={sourceData}>
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#0A1120", border: "1px solid #1E293B", borderRadius: 8 }} />
              <Bar dataKey="value" fill="#D4AF37" radius={[4, 4, 0, 0]} />
            </BarChart></ResponsiveContainer>
          </div>
        </div>
        <div className="card-luxury p-6">
          <div className="flex items-center gap-2 mb-4"><ChartBar className="text-[#D4AF37]" weight="duotone" /><h3 className="font-serif text-lg text-white">By Project</h3></div>
          <div className="h-64">
            <ResponsiveContainer><PieChart>
              <Pie data={projectData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                {projectData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0A1120", border: "1px solid #1E293B", borderRadius: 8 }} />
            </PieChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card-luxury p-6">
        <h3 className="font-serif text-lg text-white mb-4">Executive-wise</h3>
        <div className="space-y-2">
          {execData.map(e => {
            const pct = data.total_leads ? Math.round((e.value / data.total_leads) * 100) : 0;
            return (
              <div key={e.name} className="flex items-center gap-3">
                <div className="w-40 text-sm text-slate-300">{e.name}</div>
                <div className="flex-1 h-2 bg-[#1E293B] rounded-full overflow-hidden">
                  <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-16 text-right text-sm text-white">{e.value}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
